import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, BookOpen, Star, Gamepad2, Calculator, FlaskConical } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import STORY_GAMES from './StoryGameData';

const StoryGamesHub = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const grade = user?.grade_level || 7;

  const games = Object.entries(STORY_GAMES).map(([id, game]) => ({ id, ...game }));
  const gradeGames = games.filter(g => g.grade === grade);
  const otherGames = games.filter(g => g.grade !== grade);

  const subjectIcon = (s) => s === 'mathematics' ? <Calculator className="w-5 h-5" /> : s === 'science' ? <FlaskConical className="w-5 h-5" /> : <BookOpen className="w-5 h-5" />;

  return (
    <div className="min-h-screen bg-[#F4F8FB]" data-testid="story-games-hub">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#1E293B] via-[#334155] to-[#1E293B] relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-tr from-[#FF6B6B]/10 via-transparent to-[#4D96FF]/10"></div>
        <div className="relative max-w-6xl mx-auto px-4 py-8">
          <button onClick={() => navigate('/games')} className="flex items-center gap-2 text-white/70 hover:text-white font-semibold mb-4">
            <ArrowLeft className="w-5 h-5" /> Back to Brain Arena
          </button>
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-gradient-to-br from-[#FF6B6B] via-[#FFC03D] to-[#4D96FF] rounded-3xl flex items-center justify-center">
              <Gamepad2 className="w-9 h-9 text-white" />
            </div>
            <div>
              <h1 className="text-4xl font-black text-white">Story Quest Games</h1>
              <p className="text-lg text-white/70">Interactive adventures where YOU are the hero!</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Your Grade Games */}
        <h2 className="text-2xl font-black text-[#1E293B] mb-4 flex items-center gap-2">
          <Star className="w-6 h-6 text-[#FFC03D]" /> For Your Grade ({grade})
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mb-10">
          {gradeGames.map(game => (
            <StoryGameCard key={game.id} game={game} onClick={() => navigate(`/games/story/${game.id}`)} subjectIcon={subjectIcon} />
          ))}
        </div>

        {/* Other Games */}
        {otherGames.length > 0 && (
          <>
            <h2 className="text-2xl font-black text-[#1E293B] mb-4">Explore Other Grades</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {otherGames.map(game => (
                <StoryGameCard key={game.id} game={game} onClick={() => navigate(`/games/story/${game.id}`)} subjectIcon={subjectIcon} />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const StoryGameCard = ({ game, onClick, subjectIcon }) => (
  <div onClick={onClick} data-testid={`story-game-${game.id}`}
    className="group cursor-pointer bg-white rounded-3xl border-2 border-slate-100 overflow-hidden hover:-translate-y-2 hover:shadow-2xl transition-all duration-300">
    <div className={`bg-gradient-to-r ${game.color} p-5 text-white`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-bold bg-white/20 px-3 py-1 rounded-lg uppercase">Grade {game.grade}</span>
        <div className="flex items-center gap-1 bg-white/20 px-3 py-1 rounded-lg">
          {subjectIcon(game.subject)}
          <span className="text-xs font-bold capitalize">{game.subject}</span>
        </div>
      </div>
      <h3 className="text-xl font-bold">{game.title}</h3>
    </div>
    <div className="p-5">
      <p className="text-[#64748B] mb-3">{game.description}</p>
      <div className="flex items-center justify-between">
        <span className="text-xs text-[#64748B] font-semibold">{game.scenes.length} scenes</span>
        <span className="text-sm font-bold text-[#4D96FF] group-hover:underline">Play Now →</span>
      </div>
    </div>
  </div>
);

export default StoryGamesHub;
